
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

import { Database, FileText, PenTool, Search, Eye, FileBarChart } from "lucide-react";
import { Link } from 'react-router-dom';
import { Button } from "./ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';

interface ServiceItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  to: string;
}

const ServiceItem: React.FC<ServiceItemProps> = ({ icon, title, description, to }) => {
  return (
    <Link to={to} className="flex items-start p-4 gap-3 rounded-lg hover:bg-orange-50 transition-colors group">
      <div className="flex-shrink-0 mt-1">
        <div className="h-10 w-10 bg-orange-100 group-hover:bg-orange-200 rounded-md flex items-center justify-center text-orange-500">
          {icon}
        </div>
      </div>
      <div>
        <h3 className="font-medium text-gray-900 group-hover:text-orange-500 transition-colors">{title}</h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <div className="ml-auto flex items-center self-center">
        <div className="h-5 w-5 text-gray-400 group-hover:text-orange-500 group-hover:translate-x-1 transition-all">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
    </Link>
  );
};

interface ServicesDialogProps {
  trigger?: React.ReactNode;
  useHover?: boolean;
}

const ServicesDialog: React.FC<ServicesDialogProps> = ({ trigger, useHover = true }) => {
  if (useHover) {
    return (
      <HoverCard openDelay={100} closeDelay={200}>
        <HoverCardTrigger asChild>
          {trigger || <Button variant="outline">Nos Services</Button>}
        </HoverCardTrigger>
        <HoverCardContent className="w-auto p-0 max-w-4xl" align="start">
          <div className="flex flex-col">
            <div className="p-6 pb-2">
              <h3 className="text-2xl font-bold text-gray-900">Nos offres</h3>
              <p className="text-gray-500 text-sm">Découvrez tous nos services pour votre entreprise</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-4">
              <ServiceItem
                icon={<Database size={20} />}
                title="API"
                description="Pour accéder aux données"
                to="/api"
              />
              <ServiceItem
                icon={<Eye size={20} />}
                title="Surveillance"
                description="Surveillez les entreprises et Dirigeants"
                to="/surveillance"
              />
              <ServiceItem
                icon={<Search size={20} />}
                title="Prospection"
                description="Saisissez les opportunités commerciales"
                to="#"
              />
              <ServiceItem
                icon={<FileText size={20} />}
                title="Annonces légales"
                description="Publiez en ligne des annonces légales"
                to="/annonce-legales"
              />
              <ServiceItem
                icon={<FileBarChart size={20} />}
                title="Formalité d'entreprise"
                description="Création et modification d'entreprise en ligne"
                to="/formalite"
              />
              <ServiceItem
                icon={<PenTool size={20} />}
                title="Toosign"
                description="Signature électronique agréée par l'ARTCI"
                to="https://toosign.com/"
              />
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }

  // Fallback to the click dialog if useHover is false
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || <Button variant="outline">Nos Services</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-4xl p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-2xl font-bold text-gray-900">Nos offres</DialogTitle>
          <DialogDescription className="text-gray-500">
            Découvrez tous nos services pour votre entreprise
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-4">
           <ServiceItem
                icon={<Database size={20} />}
                title="API"
                description="Pour accéder aux données"
                to="/api"
              />
              <ServiceItem
                icon={<Eye size={20} />}
                title="Surveillance"
                description="Surveillez les entreprises et Dirigeants"
                to="/surveillance"
              />
              <ServiceItem
                icon={<Search size={20} />}
                title="Prospection"
                description="Saisissez les opportunités commerciales"
                to="#"
              />
              <ServiceItem
                icon={<FileText size={20} />}
                title="Annonces légales"
                description="Publiez en ligne des annonces légales"
                to="/annonce-legales"
              />
              <ServiceItem
                icon={<FileBarChart size={20} />}
                title="Formalité d'entreprise"
                description="Création et modification d'entreprise en ligne"
                to="/formalite"
              />
              <ServiceItem
                icon={<PenTool size={20} />}
                title="Toosign"
                description="Signature électronique agréée par l'ARTCI"
                to="https://toosign.com/"
              />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServicesDialog;